import React from "react";
import { scrollToTarget } from "../lib/scroll";

/**
 * IntroBanner
 * -----------
 * Full-viewport hero banner rebuilt as actual HTML/SVG (no image mockup).
 * Mirrors the client's reference layout:
 *   - dark background
 *   - big yellow U-curve framing on the left
 *   - big yellow U-curve framing on the right, with a small yellow square at
 *     the top and a small yellow circle where the curve ends
 *   - dotted white + thin yellow arcs in the top-left
 *   - two small light-blue arcs + a pinkish arc on the right
 *   - editorial photograph of a worker holding a lightbulb on the right
 *   - yellow triangle "corner" glyph above the wordmark
 *   - two-tier wordmark ("Occupational" thin / "Health & Safety" bold)
 *   - ABOUT US (blue) + CONTACT US (white) buttons, each with an offset
 *     accent rectangle sitting behind for depth
 */
export const IntroBanner = ({ onBook }) => {
  const BLUE = "#1E4DFF";       // primary button
  const BLUE_DARK = "#0A34B3";  // offset block behind ABOUT US
  const CONTACT_TXT = "#0A34B3";
  const GRAY_DARK = "#2E323A";  // offset block behind CONTACT US
  const YELLOW = "#F5B301";     // curve/glyph amber-yellow
  const YELLOW_SOFT = "#F5C56B";
  const BG = "#111114";         // near-black background

  return (
    <section
      className="relative isolate w-full overflow-hidden"
      style={{ minHeight: "100vh", background: BG }}
      data-testid="intro-banner"
      aria-label="THS Consultancy — Occupational Health & Safety"
    >
      {/* --- Right-side photograph (worker holding a lightbulb) --- */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[62%] overflow-hidden md:block">
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/job_consulting-portal-29/artifacts/27vv73x9_Screenshot%202026-09-07%20212855.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full select-none object-cover"
          style={{ objectPosition: "100% 50%", transform: "translateX(-3.5%) scale(1.09)", transformOrigin: "center center" }}
          draggable={false}
        />
        {/* Left-side dark fade — extended further right so the reference image's own dark transition strip is fully covered (no unshaded vertical seam) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(17,17,20,1) 0%, rgba(17,17,20,0.98) 22%, rgba(17,17,20,0.85) 42%, rgba(17,17,20,0.4) 62%, rgba(17,17,20,0) 82%)",
          }}
        />
        {/* Very soft top/bottom vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(17,17,20,0.35) 0%, rgba(17,17,20,0) 30%, rgba(17,17,20,0) 70%, rgba(17,17,20,0.5) 100%)",
          }}
        />
      </div>

      {/* --- Mobile-only photograph (same image as desktop, tuned for narrow viewport) --- */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden md:hidden">
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/job_consulting-portal-29/artifacts/27vv73x9_Screenshot%202026-09-07%20212855.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full select-none object-cover"
          style={{ objectPosition: "72% 50%", transform: "scale(1.55)", transformOrigin: "72% center" }}
          draggable={false}
        />
        {/* Top-heavy dark overlay so wordmark + buttons stay legible over the picture on mobile */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(17,17,20,0.94) 0%, rgba(17,17,20,0.82) 32%, rgba(17,17,20,0.55) 55%, rgba(17,17,20,0.35) 75%, rgba(17,17,20,0.6) 100%)",
          }}
        />
      </div>

      {/* --- Full-viewport decorative SVG (curves + shapes) --- */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 1600 900"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* LEFT big yellow U: vertical down on left, curves right along the bottom */}
        <path
          d="M 110 -20 L 110 700 Q 110 820 240 820 L 780 820"
          stroke={YELLOW}
          strokeWidth="3"
          fill="none"
        />
        {/* Yellow dot where the left U meets the bottom curve */}
        <circle cx="240" cy="820" r="7" fill={YELLOW} />

        {/* RIGHT big yellow U: comes down from top, curves left along a shallower arc */}
        <path
          d="M 1500 -20 L 1500 620 Q 1500 780 1360 780 L 900 780"
          stroke={YELLOW}
          strokeWidth="3"
          fill="none"
        />
        {/* Small yellow square at the top of the right U */}
        <rect x="1490" y="90" width="18" height="18" fill={YELLOW} />
        {/* Small yellow circle at the far right where a mid arc ends */}
        <circle cx="1470" cy="380" r="8" fill="none" stroke={YELLOW} strokeWidth="2" />

        {/* Inner right amber vertical + arc — echoes the reference's inner curve */}
        <path
          d="M 1360 40 L 1360 340 Q 1360 440 1260 440"
          stroke={YELLOW_SOFT}
          strokeWidth="1.8"
          fill="none"
          opacity="0.9"
        />

        {/* Dotted WHITE partial arc, top-left area — reference's dashed U */}
        <path
          d="M 320 20 L 320 300 Q 320 380 400 380"
          stroke="#FFFFFF"
          strokeWidth="1.4"
          strokeOpacity="0.55"
          strokeDasharray="3 8"
          fill="none"
        />
        {/* Thin yellow arc echoing the dotted white one */}
        <path
          d="M 360 60 L 360 300 Q 360 360 420 360"
          stroke={YELLOW}
          strokeWidth="1.2"
          strokeOpacity="0.55"
          fill="none"
        />

        {/* Two small light-blue arcs on the right — mid area */}
        <path
          d="M 1240 340 Q 1180 340 1180 410 L 1180 520"
          stroke="#A9C7E8"
          strokeWidth="1.4"
          strokeOpacity="0.75"
          fill="none"
        />
        <path
          d="M 1300 300 Q 1225 300 1225 380 L 1225 560"
          stroke="#A9C7E8"
          strokeWidth="1.4"
          strokeOpacity="0.55"
          fill="none"
        />

        {/* Pinkish arc in the upper-right */}
        <path
          d="M 1420 220 Q 1360 220 1360 300 L 1360 500"
          stroke="#F1A9A0"
          strokeWidth="1.4"
          strokeOpacity="0.7"
          fill="none"
        />

        {/* Tiny yellow square marker near the top-right dashed system */}
        <rect x="405" y="55" width="10" height="10" fill={YELLOW} />
      </svg>

      {/* --- Content (real HTML text + buttons) --- */}
      <div className="relative z-10 mx-auto flex min-h-[100vh] w-[min(1180px,92vw)] flex-col justify-center pt-24 md:pt-28 lg:pt-32">
        {/* Yellow corner "L" glyph above the wordmark */}
        <div className="mb-6 md:mb-8">
          <span
            className="inline-block"
            aria-hidden="true"
            style={{
              width: 22,
              height: 22,
              borderLeft: `4px solid ${YELLOW}`,
              borderTop: `4px solid ${YELLOW}`,
            }}
          />
        </div>

        {/* Wordmark */}
        <h1
          className="font-body leading-[1.02] tracking-[-0.02em] text-white"
          data-testid="intro-banner-title"
        >
          <span className="block text-[clamp(2.2rem,5.4vw,3.6rem)] font-light">
            Occupational
          </span>
          <span className="mt-1 block whitespace-nowrap text-[clamp(2.8rem,8.6vw,6rem)] font-extrabold">
            Health &amp; Safety
          </span>
        </h1>

        {/* Buttons row */}
        <div className="mt-10 flex flex-wrap items-center gap-4 md:mt-12">
          {/* ABOUT US — blue filled, dark-blue offset block behind */}
          <div className="relative">
            <span
              aria-hidden="true"
              className="absolute -bottom-2.5 -right-2.5 h-full w-full rounded-[6px]"
              style={{ background: BLUE_DARK }}
            />
            <button
              type="button"
              onClick={() => scrollToTarget("#about")}
              data-testid="intro-banner-about-btn"
              className="relative inline-flex items-center justify-center rounded-[6px] px-9 py-4 text-sm font-bold uppercase tracking-[0.22em] text-white transition-transform duration-200 hover:-translate-y-0.5"
              style={{ background: BLUE }}
            >
              About Us
            </button>
          </div>

          {/* CONTACT US — white filled, dark-gray offset block behind */}
          <div className="relative">
            <span
              aria-hidden="true"
              className="absolute -bottom-2.5 -right-2.5 h-full w-full rounded-[6px]"
              style={{ background: GRAY_DARK }}
            />
            <button
              type="button"
              onClick={() => (onBook ? onBook() : scrollToTarget("#book"))}
              data-testid="intro-banner-contact-btn"
              className="relative inline-flex items-center justify-center rounded-[6px] px-9 py-4 text-sm font-bold uppercase tracking-[0.22em] transition-transform duration-200 hover:-translate-y-0.5"
              style={{ background: "#FFFFFF", color: CONTACT_TXT }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntroBanner;
